import { useSurveysStore } from '@/stores/surveys.store';
import { useVocPortalStore } from '@/stores/voc-portal.store';
import ConnectSourcesGuides from '@/voc/aggregations/connect-sources-guides/connect-sources-guides.aggregation';
import ConnectSourcesItemCounts from '@/voc/aggregations/connect-sources-item-counts/connect-sources-item-counts.aggregation';
import { runAggregationOnce } from '@utils/aggregation';
import isEmpty from 'lodash/isEmpty';
import { getAllSourceConfigs } from '@/voc/api/sources.api';
import { SYNC_STATUS_ORDER } from '@/voc/constants/constants';
import { SOURCES, SURVEY_TYPE_SOURCES } from '@/voc/constants/feedback';
import { hasSegmentFlag } from '@/data/flags/local-flags';
const sources = {
    namespaced: true,
    state: {
        error: null,
        fetchingSourceConfigs: false,
        fetchingSourceItemCounts: false,
        vocSourceConfigs: [],
        filters: {
            search: '',
            types: [],
        },
    },
    mutations: {
        setFetchingSourceConfigs (state, { fetching }) {
            state.fetchingSourceConfigs = fetching;
        },
        setFetchingSourceItemCounts (state, { fetching }) {
            state.fetchingSourceItemCounts = fetching;
        },
        setVocSourceConfigs (state, { sourceConfigs }) {
            state.vocSourceConfigs = sourceConfigs;
        },
        setError (state, error) {
            state.error = error;
        },
        setFilter (state, { filterType, value }) {
            state.filters[filterType] = value;
        },
    },
    actions: {
        async fetchAllSourceConfigs ({ commit, state, dispatch, getters }, reFetch = false) {
            try {
                if (!isEmpty(state.vocSourceConfigs) && !reFetch) {
                    return;
                }
                commit('setFetchingSourceConfigs', { fetching: true });
                let sourceConfigs = await getAllSourceConfigs();
                const surveysFlagOn = hasSegmentFlag('sendSurveyResponsesToListen');
                if (!surveysFlagOn) {
                    sourceConfigs = sourceConfigs.filter((config) => config.objectName !== 'Surveys');
                }
                let guideObjects = [];
                const guideConfigs = sourceConfigs.filter((config) => config.objectName === 'Guide');
                if (guideConfigs.length > 0) {
                    const guideIds = guideConfigs.map((config) => config.objectId);
                    guideObjects = await runAggregationOnce(ConnectSourcesGuides, {
                        guideIds,
                    });
                }

                const portalConfigs = sourceConfigs.filter((config) => config.objectName === 'Portal');
                if (portalConfigs.length > 0) {
                    await useVocPortalStore().loadPortals();
                }

                const surveyConfigs = sourceConfigs.filter((config) => config.objectName === 'Surveys');
                if (surveyConfigs.length > 0 && surveysFlagOn) {
                    await useSurveysStore().loadAll().catch(() => {});
                }

                const newSourceConfigs = [];
                const configSummary = new Set();

                sourceConfigs.forEach((config) => {
                    let isNps = false;
                    if (config.objectName === 'Guide') {
                        const guide = guideObjects.find((guide) => guide.id === config.objectId);
                        isNps = guide?.polls?.some((poll) => poll.attributes.type === 'NPSRating');

                        newSourceConfigs.push({
                            ...config,
                            sourceName: guide?.name || 'Deleted Guide',
                            sourceType: getters.getSourceType(config, isNps),
                            isNps: !!isNps,
                            guide,
                        });
                    } else if (config.objectName === 'Portal') {
                        const portal = useVocPortalStore().getPortalById(config.objectId);
                        newSourceConfigs.push({
                            ...config,
                            sourceName: portal?.internalName || 'Deleted idea portal',
                            sourceType: getters.getSourceType(config, false),
                        });
                    } else if (config.objectName === 'Surveys') {
                        const survey = useSurveysStore().surveyById(config.objectId);
                        newSourceConfigs.push({
                            ...config,
                            sourceName: survey?.name || 'Deleted Survey',
                            sourceType: getters.getSourceType(config, false, survey?.surveyType),
                            survey,
                        });
                    } else {
                        newSourceConfigs.push({
                            ...config,
                            sourceName: config.displayName
                                ? config.displayName
                                : getters.getSourceName(config.objectName, config.objectId),
                            sourceType: getters.getSourceType(config, false),
                        });
                    }
                    if (isNps) {
                        configSummary.add('NPS');
                    } else {
                        configSummary.add(config.objectName);
                    }
                });
                const { visitor, account } = window.pendo.getSerializedMetadata();
                window.pendo.updateOptions({
                    visitor,
                    account: {
                        ...account,
                        listen_connected_sources: [...configSummary],
                        listen_connected_sources_count_unique: configSummary.size,
                        listen_connected_sources_count_total: sourceConfigs.length,
                    },
                });

                commit('setVocSourceConfigs', { sourceConfigs: newSourceConfigs });
                commit('setFetchingSourceConfigs', { fetching: false });
                if (newSourceConfigs.length > 0) {
                    // sort by source type so we can force the order of the groupBy on the table
                    dispatch('sortByGroupName');

                    dispatch('fetchSourceConfigCounts');
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        sortByGroupName ({ commit, state, getters }) {
            const sourceConfigs = [...state.vocSourceConfigs];
            const { sourceTypes } = getters;
            sourceConfigs.sort((a, b) => {
                const aIndex = sourceTypes.indexOf(a.sourceType);
                const bIndex = sourceTypes.indexOf(b.sourceType);

                return aIndex - bIndex;
            });
            commit('setVocSourceConfigs', { sourceConfigs });
        },
        async fetchSourceConfigCounts ({ commit, state }) {
            try {
                commit('setFetchingSourceItemCounts', { fetching: true });
                const sourceConfigs = state.vocSourceConfigs;
                const sourceConfigCounts = await runAggregationOnce(ConnectSourcesItemCounts, { sourceConfigs });
                const updatedSourceConfigs = sourceConfigs.map((config) => {
                    const sourceConfigCount = sourceConfigCounts.find(
                        (configCount) => configCount.sourceConfigId === config.objectId,
                    );

                    return {
                        ...config,
                        itemCount: sourceConfigCount?.itemCount || 0,
                        lastItemCapturedAt: sourceConfigCount?.lastItemCapturedAt || '--',
                    };
                });
                commit('setVocSourceConfigs', { sourceConfigs: updatedSourceConfigs });
                commit('setFetchingSourceItemCounts', { fetching: false });
            } catch (error) {
                commit('setFetchingSourceItemCounts', { fetching: false });
                throw error;
            }
        },
        async updateSourceConfigList ({ commit, state, getters }, { sourceConfig }) {
            try {
                const foundSourceConfig = state.vocSourceConfigs.find(
                    (config) => config.objectId === sourceConfig.objectId,
                );
                if (foundSourceConfig) {
                    // update existing source config
                    const updatedSourceConfigs = state.vocSourceConfigs.map((config) => {
                        if (config.objectId === sourceConfig.objectId) {
                            return {
                                ...config,
                                ...sourceConfig,
                            };
                        }

                        return config;
                    });
                    commit('setVocSourceConfigs', { sourceConfigs: updatedSourceConfigs });
                } else {
                    // add new source config
                    let fullSourceConfig = {
                        ...sourceConfig,
                        sourceName: sourceConfig.displayName
                            ? sourceConfig.displayName
                            : getters.getSourceName(sourceConfig.objectName, sourceConfig.objectId),
                        sourceType: getters.getSourceType(sourceConfig, false),
                    };
                    if (sourceConfig.objectName === 'Guide') {
                        const guideAggResponse = await runAggregationOnce(ConnectSourcesGuides, {
                            guideIds: [fullSourceConfig.objectId],
                        });
                        const guideObject = guideAggResponse[0] || {};
                        fullSourceConfig = {
                            ...fullSourceConfig,
                            sourceName: guideObject.name || 'Deleted Guide',
                            isNps: guideObject.polls?.some((poll) => poll.attributes.type === 'NPSRating'),
                            guide: guideObject,
                            itemCount: 0,
                        };
                    } else if (sourceConfig.objectName === 'Surveys' && hasSegmentFlag('sendSurveyResponsesToListen')) {
                        await useSurveysStore().loadAll().catch(() => {});
                        const survey = useSurveysStore().surveyById(sourceConfig.objectId);
                        fullSourceConfig = {
                            ...fullSourceConfig,
                            sourceName: survey?.name || 'Deleted Survey',
                            sourceType: getters.getSourceType(sourceConfig, false, survey?.surveyType),
                            survey,
                            itemCount: 0,
                        };
                    }
                    commit('setVocSourceConfigs', { sourceConfigs: [...state.vocSourceConfigs, fullSourceConfig] });
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        removeSourceConfig ({ commit, state }, { sourceConfigId }) {
            const updatedSourceConfigs = state.vocSourceConfigs.filter((config) => config.id !== sourceConfigId);
            commit('setVocSourceConfigs', { sourceConfigs: updatedSourceConfigs });
        },
    },
    getters: {
        getSourceName: () => (objectName, objectId) => {
            const sourceConfig = SOURCES[objectName];
            if (sourceConfig && sourceConfig.connectSourcesTitle) {
                return sourceConfig.connectSourcesTitle(objectId);
            }

            return objectName;
        },
        getSourceType: () => (source, isNps, surveyType) => {
            let sourceName = source.objectName;

            if (source.objectName === 'Guide' && isNps) {
                sourceName = 'NPS';
            } else if (source.objectName === 'Surveys' && SURVEY_TYPE_SOURCES[surveyType]) {
                return SURVEY_TYPE_SOURCES[surveyType].sourceType;
            }

            const sourceType = SOURCES[sourceName]?.sourceType;

            return sourceType;
        },
        getFilter: (state) => (filterType) => {
            return state.filters[filterType];
        },
        sourceTypes: (state) => {
            const sourceTypes = state.vocSourceConfigs.map((config) => config.sourceType);

            const sourceMapSet = new Set(sourceTypes);

            return Array.from(sourceMapSet);
        },
        configsInError: (state) => {
            return state.vocSourceConfigs.filter((config) => config.initSync?.status === 'failed');
        },
        inProgressSourceConfigs: (state) => {
            const inProgressSourceConfigs = state.vocSourceConfigs.filter(
                (config) => SYNC_STATUS_ORDER[config.initSync?.status] === 0,
            );

            return inProgressSourceConfigs;
        },
    },
};

export default sources;
